import React, { useState, ChangeEvent } from "react"
import { ModalPanel, Title, Button, MultiLineTextInput } from "dash-styled"
import { CollaboratorRole } from "../../../../../Models/API/CollaboratorModel"
import { CollaboratorRoleChooser } from "./CollaboratorRoleChooser"
import { useAsyncCommand } from "../../../../../Hooks/useAsyncCommand"

interface Props {
    onCancel: () => void
    onSendInvite: (fields: InviteSubmitFields) => Promise<void>
    viewPermissions: () => void
}

export interface InviteSubmitFields {
    emails: string[]
    role: CollaboratorRole
}

interface InviteFormFieldsInvalid {
    emails?: string
    role?: CollaboratorRole
    projects?: number[]
    valid: false
}

interface InviteFormFieldsValid {
    emails: string
    role: CollaboratorRole
    projects: number[]
    valid: true
}

export type InviteFormFields = InviteFormFieldsValid | InviteFormFieldsInvalid

function isValidForm(f: InviteFormFields): f is InviteFormFieldsValid {
    return !(f.emails === undefined || f.role === undefined)
}

export const CollaboratorInviteModal: React.FC<Props> = ({
    onSendInvite,
    onCancel,
    viewPermissions
}: Props) => {
    const [inviteForm, setInviteForm] = useState<InviteFormFields | undefined>({
        valid: false
    })

    const { perform, isProcessing } = useAsyncCommand(
        inviteForm?.valid
            ? () => {
                  const emails = inviteForm.emails
                      .split(RegExp("[,;\\s\\n\\t]"))
                      .filter((i: string) => i !== "")
                  return onSendInvite({
                      emails,
                      role: inviteForm.role
                  })
              }
            : undefined
    )

    const updateInviteForm =
        (key: "emails" | "role") =>
        (value: string | number | CollaboratorRole) => {
            const form = { ...inviteForm } as InviteFormFields
            switch (key) {
                case "emails":
                    form.emails =
                        typeof value === "string" ? value : form.emails
                    form.emails = form.emails || undefined
                    break
                case "role":
                    form.role = value as CollaboratorRole
                    break
            }

            form.valid = isValidForm(form)

            setInviteForm(form)
        }

    const handleEmailChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => updateInviteForm("emails")(e.currentTarget.value)

    return (
        <ModalPanel onClose={onCancel} className="w-full max-w-3xl">
            <Title component="h2" variant="heading2" weight="semibold">
                Invite people
            </Title>
            <div className="p-4">
                <MultiLineTextInput
                    className="mb-1"
                    label="Email address"
                    variant="bordered"
                    newlineExpanding={true}
                    inputProps={{
                        value: inviteForm?.emails || "",
                        onChange: handleEmailChange,
                        placeholder:
                            "e.g. your_email@company.com, another_email@company.com"
                    }}
                />
                <CollaboratorRoleChooser
                    label="Role"
                    className="mb-3"
                    value={inviteForm?.role}
                    onChange={updateInviteForm("role")}
                    onViewPermissions={viewPermissions}
                />
            </div>
            <Button
                onClick={perform}
                className="w-48 mx-auto"
                disabled={isProcessing || !inviteForm?.valid}
            >
                {isProcessing ? "Sending..." : "Send invite(s)"}
            </Button>
        </ModalPanel>
    )
}
